import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Code, Cloud, Lightbulb, Headphones, 
  Server, Shield, Cpu, Settings,
  GitBranch, Database, Lock, Layers, BarChart3,
  Users, FileSearch, Zap, Wrench, Clock, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const services = [
  {
    id: 'custom-development',
    title: 'Custom Software Development',
    description: 'Design and develop secure, scalable web and mobile applications tailored to business needs.',
    icon: Code,
    gradient: 'from-blue-500 to-indigo-500',
    iconBg: 'bg-blue-500',
    features: [
      { text: 'Full-stack web & mobile application development', icon: Layers },
      { text: 'SaaS platforms & internal tools', icon: Server },
      { text: 'API development & third-party integrations', icon: GitBranch },
      { text: 'Authentication, permissions, and security controls', icon: Lock },
      { text: 'Scalable, maintainable application architecture', icon: Database },
    ]
  },
  {
    id: 'cloud-infrastructure',
    title: 'Cloud Infrastructure & DevOps',
    description: 'Architect, deploy, and manage cloud environments optimized for performance, availability, and cost.',
    icon: Cloud,
    gradient: 'from-indigo-500 to-violet-500',
    iconBg: 'bg-indigo-500',
    features: [
      { text: 'Cloud architecture on AWS, Azure, or GCP', icon: Cloud },
      { text: 'High availability & auto-scaling systems', icon: Zap },
      { text: 'CI/CD pipelines & deployment automation', icon: GitBranch },
      { text: 'Cloud cost optimization & monitoring', icon: BarChart3 },
      { text: 'Security-first infrastructure configuration', icon: Shield },
    ]
  },
  {
    id: 'it-consulting',
    title: 'IT Consulting & Technical Strategy',
    description: 'Provide strategic guidance to support informed technology decisions and long-term growth.',
    icon: Lightbulb,
    gradient: 'from-violet-500 to-purple-500',
    iconBg: 'bg-violet-500',
    features: [
      { text: 'Technology assessments & system audits', icon: FileSearch },
      { text: 'Architecture reviews & optimization', icon: Settings },
      { text: 'Security planning & risk mitigation', icon: Shield },
      { text: 'Digital transformation strategy', icon: Cpu },
      { text: 'Fractional CTO & technical advisory', icon: Users },
    ]
  },
  {
    id: 'managed-services',
    title: 'Managed Services & Support',
    description: 'Ongoing support and maintenance to ensure systems remain secure, stable, and performant.',
    icon: Headphones,
    gradient: 'from-purple-500 to-pink-500',
    iconBg: 'bg-purple-500',
    features: [
      { text: 'Proactive system monitoring', icon: BarChart3 },
      { text: 'Maintenance, updates, and patching', icon: Wrench },
      { text: 'Performance optimization', icon: Zap },
      { text: 'Incident response & troubleshooting', icon: AlertCircle },
      { text: 'Priority technical support', icon: Clock },
    ]
  }
];

const ServiceCard = ({ service, index }) => {
  const IconComponent = service.icon;
  const isEven = index % 2 === 0;
  
  return (
    <div 
      id={service.id}
      className={`py-16 sm:py-20 ${isEven ? 'bg-white' : 'bg-gray-50'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Service Info */}
          <div className={`${!isEven ? 'lg:order-2' : ''}`}>
            <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-r ${service.gradient} flex items-center justify-center mb-6`}>
              <IconComponent className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-black mb-4">
              {service.title}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              {service.description}
            </p>
          </div>
          
          {/* Features List */}
          <div className={`${!isEven ? 'lg:order-1' : ''}`}>
            <div className="group relative">
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${service.gradient} rounded-2xl opacity-50 group-hover:opacity-100 blur-sm transition-all duration-500`}></div>
              <div className="relative bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 group-hover:border-transparent transition-all duration-300">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-6">
                  What's Included
                </h3>
                <div className="space-y-4">
                  {service.features.map((feature, idx) => {
                    const FeatureIcon = feature.icon;
                    return (
                      <div key={idx} className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${service.gradient} bg-opacity-10 flex items-center justify-center flex-shrink-0`}>
                          <FeatureIcon className="h-5 w-5 text-gray-700" />
                        </div>
                        <span className="text-gray-700 text-sm sm:text-base pt-2">
                          {feature.text}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServicesPage = () => {
  useEffect(() => {
    // Handle hash navigation for section links
    const hash = window.location.hash;
    if (hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Las Vegas IT Services - Custom Development, Cloud & Consulting"
        description="Fearless IT provides Las Vegas businesses with custom software development, cloud infrastructure, DevOps, and IT consulting services. Nevada's trusted tech partner for SaaS development and web applications."
        keywords="Las Vegas IT services, Las Vegas custom software development, Nevada cloud infrastructure, Las Vegas DevOps, Nevada IT consulting, Las Vegas SaaS development, Henderson software services, Las Vegas AWS Azure GCP"
        url="/our-services"
        isLocalPage={true}
        serviceName="IT Services"
      />
      
      {/* Navigation */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-12 pb-6 sm:pt-16 sm:pb-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-3 sm:mb-4">
            Our Services
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-6">
            Modern technology services built for scale
          </p>
          {/* Fearless IT Icon */}
          <img 
            src="https://customer-assets.emergentagent.com/job_f638f114-9111-40a2-b23c-9828d06c5dac/artifacts/mue55l63_1.%20FearlessIT_Icon.png"
            alt="Fearless IT"
            className="h-20 sm:h-24 mx-auto"
          />
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-6 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <a
                  key={service.id}
                  href={`#${service.id}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-black transition-colors text-sm sm:text-base"
                >
                  <IconComponent className="h-4 w-4" />
                  <span className="hidden sm:inline">{service.title.split(' ')[0]}</span>
                  <span className="sm:hidden">{service.title.split(' ')[0]}</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Sections */}
      {services.map((service, index) => (
        <ServiceCard key={service.id} service={service} index={index} />
      ))}

      {/* CTA Section */}
      <section className="py-20 sm:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black mb-4">
            Ready to get started?
          </h2>
          <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Let's discuss your project requirements and find the right solution for your business.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all"
            >
              Start a Project
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
};

export default ServicesPage;
